/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import App from "App";
import { store } from "./store";

// Material Dashboard 2 React Context Provider
import { MaterialUIControllerProvider } from "context";
import { Auth0Provider } from "@auth0/auth0-react";

// Environment configuration - simple two-environment setup (local or integration)
let isLocalEnv = process.env.REACT_APP_LOCAL_ENABLED === "true";
let environment = isLocalEnv ? "local" : "integration";

// Runtime environment detection - always takes precedence over build-time variables
if (window.location.hostname === "integration.dashboard.chefaicookware.com") {
  console.log("⚠️ Runtime detection: Using integration environment settings");
  isLocalEnv = false;
  environment = "integration";
}

// Auth0 configuration
const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  redirectUri: isLocalEnv
    ? process.env.REACT_APP_REDIRECT_URI
    : "https://integration.dashboard.chefaicookware.com",
  // Explicit logout URL based on environment
  logoutUri: isLocalEnv ? "http://localhost:3000" : "https://chefaicookware.com",
};

// Validate Auth0 configuration
if (!auth0Config.domain || !auth0Config.clientId) {
  console.error("Auth0 configuration is missing. Please check your environment variables.");
}

console.log("Application Environment:", {
  environment,
  isLocalEnv,
  auth0Domain: auth0Config.domain,
  redirectUri: auth0Config.redirectUri,
  logoutUri: auth0Config.logoutUri,
  hostname: window.location.hostname,
});

const container = document.getElementById("app");
const root = createRoot(container);

// Determine the basename based on environment and domain
const isProduction = process.env.NODE_ENV === "production";
const isCustomDomain = window.location.hostname.includes("chefaicookware.com");

// For GitHub Pages deployment, we need a basename when not on custom domain
// For custom domain (integration.dashboard.chefaicookware.com), we don't need a basename
let basename = "";
if (isProduction && !isCustomDomain && !isLocalEnv) {
  basename = "/chefai-dashboard-integration";
}

// Log routing configuration for debugging
console.log("Router configuration:", {
  basename,
  isProduction,
  isLocalEnv,
  isCustomDomain,
  environment,
  hostname: window.location.hostname,
});

// Render the app with BrowserRouter and appropriate basename
root.render(
  <Auth0Provider
    domain={auth0Config.domain}
    clientId={auth0Config.clientId}
    authorizationParams={{
      redirect_uri: auth0Config.redirectUri,
      audience: auth0Config.audience,
      scope: "openid profile email",
    }}
    logoutParams={{
      returnTo: auth0Config.logoutUri,
    }}
    cacheLocation="localstorage"
    useRefreshTokens={true}
    useRefreshTokensFallback={true}
  >
    <BrowserRouter basename={basename}>
      <MaterialUIControllerProvider>
        <Provider store={store}>
          <App />
        </Provider>
      </MaterialUIControllerProvider>
    </BrowserRouter>
  </Auth0Provider>
);
